/**
 * AI Orchestration Dashboard
 * Unified monitoring and management for all AI activities across the platform
 */

import React, { useState, useEffect } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Activity,
  Brain,
  DollarSign,
  TrendingUp,
  Zap,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Database,
  RefreshCw,
  Filter,
  Cpu,
  Sparkles,
  MessageSquare,
  FileText,
  Users,
  Shield,
  Settings
} from 'lucide-react';
import {
  getDashboardStats,
  listAITasks,
  getProviderMetrics,
  getBudgetStatus,
  getDailyPerformance,
  getWorkflows,
  getLeakageSummary,
  getTaskOutcomes,
  getBenchmarks,
  getEmployerAgenticWorkflows,
  getCategoryLabel,
  getProviderLabel,
  getStatusColor,
  AITaskSummary,
  AIProviderMetrics,
  AIBudgetStatus,
  DashboardStats,
  AITaskCategory,
  AITaskStatus,
  AIProvider,
  DailyPerformance,
  AIWorkflow,
  LeakageSummary,
  AITaskOutcome,
  AIBenchmark,
  EmployerAgenticWorkflow
} from '@/services/api/aiOrchestration';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { useTabbedChrome } from '@/components/weavein';

type TabType = 'overview' | 'tasks' | 'providers' | 'budget' | 'quality' | 'performance' | 'workflows';

export const AIOrchestrationDashboard: React.FC = () => {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [tasks, setTasks] = useState<AITaskSummary[]>([]);
  const [providerMetrics, setProviderMetrics] = useState<AIProviderMetrics[]>([]);
  const [budgetStatus, setBudgetStatus] = useState<AIBudgetStatus | null>(null);
  const [dailyPerformance, setDailyPerformance] = useState<DailyPerformance[]>([]);
  const [workflows, setWorkflows] = useState<AIWorkflow[]>([]);
  const [employerWorkflows, setEmployerWorkflows] = useState<EmployerAgenticWorkflow[]>([]);
  const [leakageSummary, setLeakageSummary] = useState<LeakageSummary[]>([]);
  const [outcomes, setOutcomes] = useState<AITaskOutcome[]>([]);
  const [benchmarks, setBenchmarks] = useState<AIBenchmark[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false); // Disabled by default

  // Filters
  const [statusFilter, setStatusFilter] = useState<AITaskStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<AITaskCategory | 'all'>('all');
  const [providerFilter, setProviderFilter] = useState<AIProvider | 'all'>('all');

  const fetchData = async () => {
    try {
      setError(null);
      const [statsData, tasksData, metricsData, budgetData, performanceData, workflowsData, employerWorkflowsData, leakageData, outcomesData, benchmarksData] = await Promise.all([
        getDashboardStats(),
        listAITasks({
          status: statusFilter !== 'all' ? statusFilter : undefined,
          task_category: categoryFilter !== 'all' ? categoryFilter : undefined,
          ai_provider: providerFilter !== 'all' ? providerFilter : undefined,
          limit: 50
        }),
        getProviderMetrics(7),
        getBudgetStatus(),
        getDailyPerformance({ days: 30 }),
        getWorkflows({ limit: 50 }),
        getEmployerAgenticWorkflows({ limit: 50 }),
        getLeakageSummary(30),
        getTaskOutcomes({ limit: 50 }),
        getBenchmarks({ is_active: true })
      ]);

      setStats(statsData);
      setTasks(tasksData);
      setProviderMetrics(metricsData);
      setBudgetStatus(budgetData);
      setDailyPerformance(performanceData);
      setWorkflows(workflowsData);
      setEmployerWorkflows(employerWorkflowsData);
      setLeakageSummary(leakageData);
      setOutcomes(outcomesData);
      setBenchmarks(benchmarksData);
    } catch (error: any) {
      console.error('Error fetching AI orchestration data:', error);
      setError(error?.response?.status === 405 ? 'AI Orchestration backend not yet deployed. Coming soon!' : 'Failed to load AI data. Please try again later.');
      // Disable auto-refresh on error
      setAutoRefresh(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, categoryFilter, providerFilter]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchData();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, statusFilter, categoryFilter, providerFilter]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('ai.loading_ai_orchestration_center', 'Loading AI Orchestration Dashboard...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="max-w-2xl w-full">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                <Brain className="h-12 w-12 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('ai.ai_orchestration_center', 'AI Orchestration Dashboard')}</h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">{error}</p>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6 mb-6 text-left">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                {t('ai.coming_soon_unified_ai_monitoring', 'Coming Soon: Unified AI Monitoring')}
              </h3>
              <ul className="space-y-2 text-sm text-blue-700 dark:text-blue-300">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{t('ai.realtime_tracking_of_all_ai', 'Real-time tracking of all AI operations across the platform')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{t('ai.cost_monitoring_and_budget_management', 'Cost monitoring and budget management for AI services')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{t('ai.quality_assurance_with_confidence_scores', 'Quality assurance with confidence scores and validation')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Provider performance analytics (Claude, GPT, Gemini, etc.)</span>
                </li>
              </ul>
            </div>

            <button
              onClick={() => { setLoading(true); fetchData(); }}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
            >
              <RefreshCw className="h-4 w-4" />
              {t('ai.try_again', 'Try Again')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            {!tabbed && (
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Brain className="h-6 w-6 text-blue-600" />
                {t('ai.ai_orchestration_center_1', 'AI Orchestration Center')}
              </h1>
            )}
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('ai.unified_monitoring_for_all_ai', 'Unified monitoring for all AI activities')}</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                autoRefresh
                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                  : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto-refresh On' : 'Auto-refresh Off'}
            </button>

            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              {t('ai.refresh_now', 'Refresh Now')}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
          <TabButton
            active={activeTab === 'overview'}
            onClick={() => setActiveTab('overview')}
            icon={<Activity className="h-4 w-4" />}
            label={t('ai.overview', 'Overview')}
          />
          <TabButton
            active={activeTab === 'tasks'}
            onClick={() => setActiveTab('tasks')}
            icon={<Zap className="h-4 w-4" />}
            label={t('ai.tasks_1', 'Tasks')}
            badge={stats?.running_tasks || 0}
          />
          <TabButton
            active={activeTab === 'providers'}
            onClick={() => setActiveTab('providers')}
            icon={<Cpu className="h-4 w-4" />}
            label={t('ai.providers', 'Providers')}
          />
          <TabButton
            active={activeTab === 'budget'}
            onClick={() => setActiveTab('budget')}
            icon={<DollarSign className="h-4 w-4" />}
            label={t('ai.budget', 'Budget')}
          />
          <TabButton
            active={activeTab === 'quality'}
            onClick={() => setActiveTab('quality')}
            icon={<Shield className="h-4 w-4" />}
            label={t('ai.quality', 'Quality')}
          />
          <TabButton
            active={activeTab === 'performance'}
            onClick={() => setActiveTab('performance')}
            icon={<BarChart3 className="h-4 w-4" />}
            label={t('ai.performance', 'Performance')}
          />
          <TabButton
            active={activeTab === 'workflows'}
            onClick={() => setActiveTab('workflows')}
            icon={<TrendingUp className="h-4 w-4" />}
            label={t('ai.workflows', 'Workflows')}
          />
        </div>
      </div>

      {/* Content */}
      {activeTab === 'overview' && (
        <OverviewTab stats={stats} tasks={tasks} budgetStatus={budgetStatus} providerMetrics={providerMetrics} />
      )}

      {activeTab === 'tasks' && (
        <TasksTab
          tasks={tasks}
          statusFilter={statusFilter}
          categoryFilter={categoryFilter}
          providerFilter={providerFilter}
          onStatusFilterChange={setStatusFilter}
          onCategoryFilterChange={setCategoryFilter}
          onProviderFilterChange={setProviderFilter}
        />
      )}

      {activeTab === 'providers' && (
        <ProvidersTab providerMetrics={providerMetrics} />
      )}

      {activeTab === 'budget' && (
        <BudgetTab budgetStatus={budgetStatus} stats={stats} />
      )}

      {activeTab === 'quality' && (
        <QualityTab tasks={tasks} stats={stats} />
      )}

      {activeTab === 'performance' && (
        <PerformanceTab
          dailyPerformance={dailyPerformance}
          benchmarks={benchmarks}
          outcomes={outcomes}
          stats={stats}
        />
      )}

      {activeTab === 'workflows' && (
        <WorkflowsTab
          workflows={workflows}
          employerWorkflows={employerWorkflows}
          leakageSummary={leakageSummary}
        />
      )}
    </div>
  );
};

// Tab Button Component
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, label, badge }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
      active
        ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-medium'
        : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'
    }`}
  >
    {icon}
    {label}
    {badge !== undefined && badge > 0 && (
      <span className="ml-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full font-medium">
        {badge}
      </span>
    )}
  </button>
);

// Overview Tab Component
interface OverviewTabProps {
  stats: DashboardStats | null;
  tasks: AITaskSummary[];
  budgetStatus: AIBudgetStatus | null;
  providerMetrics: AIProviderMetrics[];
}

const OverviewTab: React.FC<OverviewTabProps> = ({ stats, tasks, budgetStatus, providerMetrics }) => {
  const { t } = useAppTranslation();
  if (!stats) return null;

  const runningTasks = tasks.filter(task => task.status === 'running');

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title={t('ai.tasks_today', 'Tasks Today')}
          value={stats.total_tasks_today}
          icon={<Activity className="h-6 w-6 text-blue-600" />}
          trend={`${stats.completed_today} completed`}
          color="blue"
        />
        <MetricCard
          title={t('ai.running_now', 'Running Now')}
          value={stats.running_tasks}
          icon={<Zap className="h-6 w-6 text-blue-600" />}
          trend={`${stats.queued_tasks} queued`}
          color="blue"
        />
        <MetricCard
          title={t('ai.cost_today', 'Cost Today')}
          value={`$${typeof stats.total_cost_today === 'number' ? stats.total_cost_today.toFixed(2) : '0.00'}`}
          icon={<DollarSign className="h-6 w-6 text-green-600" />}
          trend={budgetStatus?.budget_exceeded ? 'Budget exceeded!' : 'Within budget'}
          color="green"
        />
        <MetricCard
          title={t('ai.avg_performance', 'Avg Performance')}
          value={stats.avg_duration_ms ? `${(stats.avg_duration_ms / 1000).toFixed(1)}s` : 'N/A'}
          icon={<Clock className="h-6 w-6 text-orange-600" />}
          trend={stats.avg_confidence_score ? `${(stats.avg_confidence_score * 100).toFixed(1)}% confidence` : ''}
          color="orange"
        />
      </div>

      {/* Active Tasks Stream */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600" />
          {t('ai.active_tasks_stream', 'Active Tasks Stream')}
        </h2>

        {runningTasks.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Sparkles className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>{t('ai.no_tasks_currently_running', 'No tasks currently running')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {runningTasks.slice(0, 5).map(task => (
              <TaskStreamItem key={task.id} task={task} />
            ))}
          </div>
        )}
      </div>

      {/* Budget Overview */}
      {budgetStatus && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            {t('ai.budget_status', 'Budget Status')}
          </h2>

          <div className="space-y-4">
            <BudgetBar
              label={t('ai.daily_budget_1', 'Daily Budget')}
              spent={budgetStatus.current_day_spend}
              limit={budgetStatus.daily_budget_usd || 0}
              color="blue"
            />
            <BudgetBar
              label={t('ai.monthly_budget_1', 'Monthly Budget')}
              spent={budgetStatus.current_month_spend}
              limit={budgetStatus.monthly_budget_usd || 0}
              color="blue"
            />
          </div>
        </div>
      )}

      {/* Provider Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Cpu className="h-5 w-5 text-blue-600" />
          Provider Distribution (Last 7 Days)
        </h2>

        <div className="space-y-3">
          {providerMetrics.slice(0, 5).map((metric, index) => (
            <ProviderDistributionItem key={index} metric={metric} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Tasks Tab Component
interface TasksTabProps {
  tasks: AITaskSummary[];
  statusFilter: AITaskStatus | 'all';
  categoryFilter: AITaskCategory | 'all';
  providerFilter: AIProvider | 'all';
  onStatusFilterChange: (status: AITaskStatus | 'all') => void;
  onCategoryFilterChange: (category: AITaskCategory | 'all') => void;
  onProviderFilterChange: (provider: AIProvider | 'all') => void;
}

const TasksTab: React.FC<TasksTabProps> = ({
  tasks,
  statusFilter,
  categoryFilter,
  providerFilter,
  onStatusFilterChange,
  onCategoryFilterChange,
  onProviderFilterChange
}) => {
  const { t } = useAppTranslation();
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4">
        <div className="flex items-center gap-4">
          <Filter className="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <select
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value as AITaskStatus | 'all')}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('ai.all_statuses', 'All Statuses')}</option>
            <option value="queued">{t('ai.queued', 'Queued')}</option>
            <option value="running">{t('ai.running', 'Running')}</option>
            <option value="completed">{t('ai.completed', 'Completed')}</option>
            <option value="failed">{t('ai.failed', 'Failed')}</option>
            <option value="cancelled">{t('ai.cancelled', 'Cancelled')}</option>
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => onCategoryFilterChange(e.target.value as AITaskCategory | 'all')}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('ai.all_categories', 'All Categories')}</option>
            <option value="external_integration">{t('ai.external_integration', 'External Integration')}</option>
            <option value="voice_ai">{t('ai.voice_ai', 'Voice AI')}</option>
            <option value="data_enrichment">{t('ai.data_enrichment', 'Data Enrichment')}</option>
            <option value="communication_ai">{t('ai.communication_ai', 'Communication AI')}</option>
            <option value="predictive_analytics">{t('ai.predictive_analytics', 'Predictive Analytics')}</option>
            <option value="document_processing">{t('ai.document_processing', 'Document Processing')}</option>
            <option value="workflow_automation">{t('ai.workflow_automation', 'Workflow Automation')}</option>
            <option value="quality_assurance">{t('ai.quality_assurance', 'Quality Assurance')}</option>
          </select>

          <select
            value={providerFilter}
            onChange={(e) => onProviderFilterChange(e.target.value as AIProvider | 'all')}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('ai.all_providers', 'All Providers')}</option>
            <option value="anthropic_claude">{t('ai.anthropic_claude', 'Anthropic Claude')}</option>
            <option value="openai_gpt">{t('ai.openai_gpt', 'OpenAI GPT')}</option>
            <option value="google_gemini">{t('ai.google_gemini', 'Google Gemini')}</option>
            <option value="assembly_ai">{t('ai.assemblyai', 'AssemblyAI')}</option>
            <option value="deepgram">{t('ai.deepgram', 'Deepgram')}</option>
            <option value="internal">{t('ai.internal', 'Internal')}</option>
          </select>
        </div>
      </div>

      {/* Task List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Database className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p>{t('ai.no_tasks_found_matching_your', 'No tasks found matching your filters')}</p>
            </div>
          ) : (
            tasks.map(task => (
              <TaskListItem key={task.id} task={task} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Providers Tab Component
interface ProvidersTabProps {
  providerMetrics: AIProviderMetrics[];
}

const ProvidersTab: React.FC<ProvidersTabProps> = ({ providerMetrics }) => {
  // Group metrics by provider
  const providerGroups = providerMetrics.reduce((acc, metric) => {
    const provider = metric.ai_provider;
    if (!acc[provider]) {
      acc[provider] = [];
    }
    acc[provider].push(metric);
    return acc;
  }, {} as Record<string, AIProviderMetrics[]>);

  if (providerMetrics.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
        <p className="text-gray-500 dark:text-gray-400">No provider metrics available yet. Metrics will appear as AI tasks are processed.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(providerGroups).map(([provider, metrics]) => {
          const totalTasks = metrics.reduce((sum, m) => sum + m.total_tasks, 0);
          const totalCost = metrics.reduce((sum, m) => sum + Number(m.total_cost_usd || 0), 0);
          const avgDuration = metrics.reduce((sum, m) => sum + (m.avg_duration_ms || 0), 0) / metrics.length;
          const successRate = metrics.reduce((sum, m) => sum + m.successful_tasks, 0) / Math.max(totalTasks, 1);

          return (
            <ProviderCard
              key={provider}
              provider={provider as AIProvider}
              totalTasks={totalTasks}
              totalCost={totalCost}
              avgDuration={avgDuration}
              successRate={successRate}
            />
          );
        })}
      </div>
    </div>
  );
};

// Budget Tab Component
interface BudgetTabProps {
  budgetStatus: AIBudgetStatus | null;
  stats: DashboardStats | null;
}

const BudgetTab: React.FC<BudgetTabProps> = ({ budgetStatus, stats }) => {
  const { t } = useAppTranslation();
  if (!budgetStatus || !stats) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Daily Budget */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('ai.daily_budget', 'Daily Budget')}</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">{t('ai.spent_today', 'Spent Today')}</span>
                <span className="font-semibold dark:text-gray-100">${typeof budgetStatus.current_day_spend === 'number' ? budgetStatus.current_day_spend.toFixed(2) : '0.00'}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">{t('ai.daily_limit', 'Daily Limit')}</span>
                <span className="font-semibold dark:text-gray-100">${typeof budgetStatus.daily_budget_usd === 'number' ? budgetStatus.daily_budget_usd.toFixed(2) : 'N/A'}</span>
              </div>
              {budgetStatus.daily_budget_usd && (
                <BudgetBar
                  label=""
                  spent={budgetStatus.current_day_spend}
                  limit={budgetStatus.daily_budget_usd}
                  color="blue"
                />
              )}
            </div>
          </div>
        </div>

        {/* Monthly Budget */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('ai.monthly_budget', 'Monthly Budget')}</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">{t('ai.spent_this_month', 'Spent This Month')}</span>
                <span className="font-semibold dark:text-gray-100">${typeof budgetStatus.current_month_spend === 'number' ? budgetStatus.current_month_spend.toFixed(2) : '0.00'}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600 dark:text-gray-400">{t('ai.monthly_limit', 'Monthly Limit')}</span>
                <span className="font-semibold dark:text-gray-100">${typeof budgetStatus.monthly_budget_usd === 'number' ? budgetStatus.monthly_budget_usd.toFixed(2) : 'N/A'}</span>
              </div>
              {budgetStatus.monthly_budget_usd && (
                <BudgetBar
                  label=""
                  spent={budgetStatus.current_month_spend}
                  limit={budgetStatus.monthly_budget_usd}
                  color="blue"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Token Usage */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">{t('ai.token_usage', 'Token Usage')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('ai.today', 'Today')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total_tokens_today.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('ai.this_month', 'This Month')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{budgetStatus.current_month_tokens.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('ai.monthly_limit_1', 'Monthly Limit')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {budgetStatus.monthly_token_limit?.toLocaleString() || 'Unlimited'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quality Tab Component
interface QualityTabProps {
  tasks: AITaskSummary[];
  stats: DashboardStats | null;
}

const QualityTab: React.FC<QualityTabProps> = ({ tasks, stats }) => {
  const { t } = useAppTranslation();
  const completedTasks = tasks.filter(task => task.status === 'completed');
  const failedTasks = tasks.filter(task => task.status === 'failed');
  const successRate = completedTasks.length / Math.max(tasks.length, 1);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          title={t('ai.success_rate_1', 'Success Rate')}
          value={`${(successRate * 100).toFixed(1)}%`}
          icon={<CheckCircle className="h-6 w-6 text-green-600" />}
          trend={`${completedTasks.length} of ${tasks.length} completed`}
          color="green"
        />
        <MetricCard
          title={t('ai.avg_confidence', 'Avg Confidence')}
          value={stats?.avg_confidence_score ? `${(stats.avg_confidence_score * 100).toFixed(1)}%` : 'N/A'}
          icon={<TrendingUp className="h-6 w-6 text-blue-600" />}
          trend="Across all tasks"
          color="blue"
        />
        <MetricCard
          title={t('ai.failed_tasks_1', 'Failed Tasks')}
          value={failedTasks.length}
          icon={<XCircle className="h-6 w-6 text-red-600" />}
          trend="Requires attention"
          color="red"
        />
      </div>

      {/* Failed Tasks List */}
      {failedTasks.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            {t('ai.failed_tasks', 'Failed Tasks')}
          </h3>
          <div className="space-y-3">
            {failedTasks.slice(0, 10).map(task => (
              <TaskListItem key={task.id} task={task} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Performance Tab Component
interface PerformanceTabProps {
  dailyPerformance: DailyPerformance[];
  benchmarks: AIBenchmark[];
  outcomes: AITaskOutcome[];
  stats: DashboardStats | null;
}

const PerformanceTab: React.FC<PerformanceTabProps> = ({ dailyPerformance, benchmarks, outcomes, stats }) => {
  const { t } = useAppTranslation();
  // Group performance by task type
  const performanceByType = dailyPerformance.reduce((acc, perf) => {
    const key = `${perf.task_category}:${perf.task_type}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(perf);
    return acc;
  }, {} as Record<string, DailyPerformance[]>);

  // Calculate outcome stats
  const outcomeStats = outcomes.reduce((acc, outcome) => {
    acc[outcome.outcome_status] = (acc[outcome.outcome_status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const totalOutcomes = outcomes.length;
  const successRate = totalOutcomes > 0
    ? ((outcomeStats['success'] || 0) + (outcomeStats['partial_success'] || 0)) / totalOutcomes
    : 0;

  return (
    <div className="space-y-6">
      {/* Key Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title={t('ai.overall_success_rate', 'Overall Success Rate')}
          value={`${(successRate * 100).toFixed(1)}%`}
          icon={<CheckCircle className="h-6 w-6 text-green-600" />}
          trend={`${totalOutcomes} total outcomes`}
          color="green"
        />
        <MetricCard
          title={t('ai.active_benchmarks_1', 'Active Benchmarks')}
          value={benchmarks.length}
          icon={<BarChart3 className="h-6 w-6 text-blue-600" />}
          trend="Quality thresholds"
          color="blue"
        />
        <MetricCard
          title={t('ai.avg_confidence_1', 'Avg Confidence')}
          value={stats?.avg_confidence_score ? `${(stats.avg_confidence_score * 100).toFixed(1)}%` : 'N/A'}
          icon={<TrendingUp className="h-6 w-6 text-blue-600" />}
          trend="Across all tasks"
          color="blue"
        />
        <MetricCard
          title={t('ai.task_types_tracked', 'Task Types Tracked')}
          value={Object.keys(performanceByType).length}
          icon={<Activity className="h-6 w-6 text-orange-600" />}
          trend="With performance data"
          color="orange"
        />
      </div>

      {/* Task Outcomes Distribution */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-600" />
          {t('ai.task_outcomes_distribution', 'Task Outcomes Distribution')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <OutcomeCard label={t('ai.success_1', 'Success')} count={outcomeStats['success'] || 0} color="green" />
          <OutcomeCard label={t('ai.partial_success', 'Partial Success')} count={outcomeStats['partial_success'] || 0} color="yellow" />
          <OutcomeCard label={t('ai.failure', 'Failure')} count={outcomeStats['failure'] || 0} color="red" />
          <OutcomeCard label={t('ai.timeout', 'Timeout')} count={outcomeStats['timeout'] || 0} color="orange" />
          <OutcomeCard label={t('ai.cancelled_1', 'Cancelled')} count={outcomeStats['cancelled'] || 0} color="gray" />
        </div>
      </div>

      {/* Benchmarks */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-600" />
          {t('ai.active_benchmarks', 'Active Benchmarks')}
        </h3>
        {benchmarks.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>{t('ai.no_benchmarks_configured', 'No benchmarks configured')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {benchmarks.map(benchmark => (
              <BenchmarkItem key={benchmark.id} benchmark={benchmark} />
            ))}
          </div>
        )}
      </div>

      {/* Performance by Task Type */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-600" />
          Performance by Task Type (Last 30 Days)
        </h3>
        {Object.keys(performanceByType).length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>{t('ai.no_performance_data_available', 'No performance data available')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(performanceByType).slice(0, 10).map(([key, perfs]) => {
              const [category, taskType] = key.split(':');
              const totalTasks = perfs.reduce((sum, p) => sum + p.total_tasks, 0);
              const avgSuccessRate = perfs.reduce((sum, p) => sum + p.success_rate, 0) / perfs.length;
              const avgDuration = perfs.reduce((sum, p) => sum + p.avg_duration_ms, 0) / perfs.length;
              const totalCost = perfs.reduce((sum, p) => sum + p.total_cost_usd, 0);

              return (
                <PerformanceTypeItem
                  key={key}
                  category={category as AITaskCategory}
                  taskType={taskType}
                  totalTasks={totalTasks}
                  successRate={avgSuccessRate}
                  avgDuration={avgDuration}
                  totalCost={totalCost}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Outcomes */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Activity className="h-5 w-5 text-blue-600" />
          {t('ai.recent_task_outcomes', 'Recent Task Outcomes')}
        </h3>
        {outcomes.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Database className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>{t('ai.no_outcomes_recorded', 'No outcomes recorded')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {outcomes.slice(0, 10).map(outcome => (
              <OutcomeItem key={outcome.id} outcome={outcome} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Workflows Tab Component
interface WorkflowsTabProps {
  workflows: AIWorkflow[];
  employerWorkflows: EmployerAgenticWorkflow[];
  leakageSummary: LeakageSummary[];
}

const WorkflowsTab: React.FC<WorkflowsTabProps> = ({ workflows, employerWorkflows, leakageSummary }) => {
  const { t } = useAppTranslation();
  const activeWorkflows = workflows.filter(w => w.status === 'active');
  const completedWorkflows = workflows.filter(w => w.status === 'completed');
  const failedWorkflows = workflows.filter(w => w.status === 'failed');

  // Employer workflow stats
  const activeEmployerWorkflows = employerWorkflows.filter(w => ['queued', 'in_progress', 'waiting_response'].includes(w.status));
  const completedEmployerWorkflows = employerWorkflows.filter(w => w.status === 'completed');

  const totalLeakage = leakageSummary.reduce((sum, l) => sum + l.leaked_workflows, 0);
  const avgLeakageRate = leakageSummary.length > 0
    ? leakageSummary.reduce((sum, l) => sum + l.leakage_rate, 0) / leakageSummary.length
    : 0;

  return (
    <div className="space-y-6">
      {/* Workflow Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title={t('ai.active_workflows_1', 'Active Workflows')}
          value={activeWorkflows.length + activeEmployerWorkflows.length}
          icon={<Activity className="h-6 w-6 text-blue-600" />}
          trend={`${activeEmployerWorkflows.length} employer workflows`}
          color="blue"
        />
        <MetricCard
          title={t('ai.completed_1', 'Completed')}
          value={completedWorkflows.length + completedEmployerWorkflows.length}
          icon={<CheckCircle className="h-6 w-6 text-green-600" />}
          trend="Successfully finished"
          color="green"
        />
        <MetricCard
          title={t('ai.failed_1', 'Failed')}
          value={failedWorkflows.length}
          icon={<XCircle className="h-6 w-6 text-red-600" />}
          trend="Requires attention"
          color="red"
        />
        <MetricCard
          title={t('ai.leakage_rate', 'Leakage Rate')}
          value={`${(avgLeakageRate * 100).toFixed(1)}%`}
          icon={<AlertCircle className="h-6 w-6 text-orange-600" />}
          trend={`${totalLeakage} leaked workflows`}
          color="orange"
        />
      </div>

      {/* Employer Agentic Workflows */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          {t('ai.employer_agentic_workflows', 'Employer Agentic Workflows')}
          {employerWorkflows.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full">
              {employerWorkflows.length}
            </span>
          )}
        </h3>
        {employerWorkflows.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Users className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>{t('ai.no_employer_workflows', 'No employer workflows initiated yet')}</p>
            <p className="text-sm mt-1">{t('ai.start_workflows_from_orchestration', 'Start workflows from the Employer Workflow Orchestration page')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {employerWorkflows.slice(0, 20).map(workflow => (
              <EmployerWorkflowItem key={workflow.id} workflow={workflow} />
            ))}
          </div>
        )}
      </div>

      {/* Leakage Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-orange-600" />
          {t('ai.workflow_leakage_analysis', 'Workflow Leakage Analysis')}
        </h3>
        {leakageSummary.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <TrendingUp className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>{t('ai.no_leakage_data_available', 'No leakage data available')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {leakageSummary.map((summary, index) => (
              <LeakageItem key={index} summary={summary} />
            ))}
          </div>
        )}
      </div>

      {/* Active AI Workflows */}
      {activeWorkflows.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Activity className="h-5 w-5 text-blue-600" />
            {t('ai.active_workflows', 'Active AI Workflows')}
          </h3>
          <div className="space-y-3">
            {activeWorkflows.slice(0, 10).map(workflow => (
              <WorkflowItem key={workflow.id} workflow={workflow} />
            ))}
          </div>
        </div>
      )}

      {/* All AI Workflows */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Database className="h-5 w-5 text-blue-600" />
          {t('ai.recent_workflows', 'Recent AI Workflows')}
        </h3>
        {workflows.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p>{t('ai.no_workflows_recorded', 'No AI workflows recorded')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {workflows.slice(0, 20).map(workflow => (
              <WorkflowItem key={workflow.id} workflow={workflow} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Helper Components

const MetricCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend: string;
  color: string;
}> = ({ title, value, icon, trend, color }) => {
  const iconBg: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-900/20',
    green: 'bg-green-50 dark:bg-green-900/20',
    orange: 'bg-orange-50 dark:bg-orange-900/20',
    red: 'bg-red-50 dark:bg-red-900/20',
    yellow: 'bg-yellow-50 dark:bg-yellow-900/20',
    gray: 'bg-gray-100 dark:bg-gray-700',
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
        <div className={`p-2 rounded-lg ${iconBg[color] || iconBg.blue}`}>{icon}</div>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{value}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{trend}</p>
    </div>
  );
};

const TaskStreamItem: React.FC<{ task: AITaskSummary }> = ({ task }) => {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  return (
    <div className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
      <div className="animate-pulse">
        <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
      </div>
      <div className="flex-1">
        <p className="font-medium text-gray-900 dark:text-gray-100">{task.task_name}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{getCategoryLabel(task.task_category)} • {getProviderLabel(task.ai_provider)}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-medium text-blue-600 dark:text-blue-400">{t('ai.running_1', 'Running')}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400">{fmt.time(task.started_at!)}</p>
      </div>
    </div>
  );
};

const BudgetBar: React.FC<{ label: string; spent: number; limit: number; color: string }> = ({ label, spent, limit, color }) => {
  const percentage = Math.min((spent / limit) * 100, 100);
  const colorClass = percentage > 90 ? 'bg-red-600' : percentage > 70 ? 'bg-yellow-600' : `bg-${color}-600`;

  return (
    <div>
      {label && <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{label}</p>}
      <div className="flex items-center gap-3">
        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-3">
          <div
            className={`h-full rounded-full ${colorClass} transition-all`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{percentage.toFixed(0)}%</span>
      </div>
    </div>
  );
};

const ProviderDistributionItem: React.FC<{ metric: AIProviderMetrics }> = ({ metric }) => (
  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
    <div>
      <p className="font-medium text-gray-900 dark:text-gray-100">{getProviderLabel(metric.ai_provider)}</p>
      <p className="text-sm text-gray-600 dark:text-gray-400">{metric.model_name || 'All models'}</p>
    </div>
    <div className="text-right">
      <p className="font-semibold text-gray-900 dark:text-gray-100">{metric.total_tasks} tasks</p>
      <p className="text-sm text-gray-600 dark:text-gray-400">${typeof metric.total_cost_usd === 'number' ? metric.total_cost_usd.toFixed(2) : '0.00'}</p>
    </div>
  </div>
);

const TaskListItem: React.FC<{ task: AITaskSummary }> = ({ task }) => {
  const statusColor = getStatusColor(task.status);

  return (
    <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className={`px-2 py-1 text-xs rounded-full bg-${statusColor}-100 dark:bg-${statusColor}-900/30 text-${statusColor}-700 dark:text-${statusColor}-300 font-medium`}>
              {task.status}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">{getCategoryLabel(task.task_category)}</span>
          </div>
          <p className="font-medium text-gray-900 dark:text-gray-100">{task.task_name}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">{getProviderLabel(task.ai_provider)} {task.model_name && `• ${task.model_name}`}</p>
        </div>
        <div className="text-right">
          {task.duration_ms && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{(task.duration_ms / 1000).toFixed(1)}s</p>
          )}
          {task.actual_cost_usd && (
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">${typeof task.actual_cost_usd === 'number' ? task.actual_cost_usd.toFixed(4) : '0.0000'}</p>
          )}
          {task.total_tokens && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{task.total_tokens.toLocaleString()} tokens</p>
          )}
        </div>
      </div>
    </div>
  );
};

const ProviderCard: React.FC<{
  provider: AIProvider;
  totalTasks: number;
  totalCost: number;
  avgDuration: number;
  successRate: number;
}> = ({ provider, totalTasks, totalCost, avgDuration, successRate }) => {
  const { t } = useAppTranslation();
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">{getProviderLabel(provider)}</h3>
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('ai.total_tasks', 'Total Tasks')}</span>
          <span className="font-medium dark:text-gray-100">{totalTasks}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('ai.total_cost', 'Total Cost')}</span>
          <span className="font-medium dark:text-gray-100">${typeof totalCost === 'number' ? totalCost.toFixed(2) : '0.00'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('ai.avg_duration', 'Avg Duration')}</span>
          <span className="font-medium dark:text-gray-100">{typeof avgDuration === 'number' ? (avgDuration / 1000).toFixed(1) : '0.0'}s</span>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">{t('ai.success_rate', 'Success Rate')}</span>
          <span className="font-medium dark:text-gray-100">{typeof successRate === 'number' ? (successRate * 100).toFixed(1) : '0.0'}%</span>
        </div>
      </div>
    </div>
  );
};

// Performance & Workflow Helper Components

const OutcomeCard: React.FC<{ label: string; count: number; color: string }> = ({ label, count, color }) => (
  <div className={`p-4 rounded-lg bg-${color}-50 dark:bg-${color}-900/20 border border-${color}-100 dark:border-${color}-800`}>
    <p className={`text-2xl font-bold text-${color}-700 dark:text-${color}-300`}>{count}</p>
    <p className={`text-sm text-${color}-600 dark:text-${color}-400`}>{label}</p>
  </div>
);

const BenchmarkItem: React.FC<{ benchmark: AIBenchmark }> = ({ benchmark }) => {
  const operatorLabels: Record<string, string> = {
    lt: '<',
    lte: '<=',
    gt: '>',
    gte: '>=',
    eq: '='
  };

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
      <div>
        <p className="font-medium text-gray-900 dark:text-gray-100">{benchmark.metric_name}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {getCategoryLabel(benchmark.task_category)} • {benchmark.task_type}
        </p>
      </div>
      <div className="text-right">
        <p className="font-semibold text-gray-900 dark:text-gray-100">
          {operatorLabels[benchmark.comparison_operator]} {benchmark.target_value}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Warn: {benchmark.warning_threshold} | Crit: {benchmark.critical_threshold}
        </p>
      </div>
    </div>
  );
};

const PerformanceTypeItem: React.FC<{
  category: AITaskCategory;
  taskType: string;
  totalTasks: number;
  successRate: number;
  avgDuration: number;
  totalCost: number;
}> = ({ category, taskType, totalTasks, successRate, avgDuration, totalCost }) => {
  const { t } = useAppTranslation();
  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
      <div className="flex-1">
        <p className="font-medium text-gray-900 dark:text-gray-100">{taskType}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{getCategoryLabel(category)}</p>
      </div>
      <div className="grid grid-cols-4 gap-4 text-right">
        <div>
          <p className="font-semibold text-gray-900 dark:text-gray-100">{totalTasks}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('ai.tasks', 'Tasks')}</p>
        </div>
        <div>
          <p className="font-semibold text-green-600 dark:text-green-400">{(successRate * 100).toFixed(1)}%</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('ai.success', 'Success')}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-gray-100">{(avgDuration / 1000).toFixed(1)}s</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('ai.avg_time', 'Avg Time')}</p>
        </div>
        <div>
          <p className="font-semibold text-gray-900 dark:text-gray-100">${totalCost.toFixed(2)}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('ai.cost', 'Cost')}</p>
        </div>
      </div>
    </div>
  );
};

const OutcomeItem: React.FC<{ outcome: AITaskOutcome }> = ({ outcome }) => {
  const { fmt } = useFormattedDate();
  const statusColors: Record<string, string> = {
    success: 'green',
    partial_success: 'yellow',
    failure: 'red',
    timeout: 'orange',
    cancelled: 'gray'
  };
  const color = statusColors[outcome.outcome_status] || 'gray';

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`px-2 py-1 text-xs rounded-full bg-${color}-100 dark:bg-${color}-900/30 text-${color}-700 dark:text-${color}-300 font-medium`}>
            {outcome.outcome_status.replace('_', ' ')}
          </span>
          <span className="text-sm text-gray-600 dark:text-gray-400">Task #{outcome.ai_task_id}</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {getCategoryLabel(outcome.task_category)} • {outcome.task_type}
        </p>
        {outcome.business_impact && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{outcome.business_impact}</p>
        )}
      </div>
      <div className="text-right">
        <div className="flex items-center gap-2">
          {outcome.benchmarks_passed > 0 && (
            <span className="text-xs text-green-600 dark:text-green-400">{outcome.benchmarks_passed} passed</span>
          )}
          {outcome.benchmarks_failed > 0 && (
            <span className="text-xs text-red-600 dark:text-red-400">{outcome.benchmarks_failed} failed</span>
          )}
          {outcome.benchmarks_warning > 0 && (
            <span className="text-xs text-yellow-600 dark:text-yellow-400">{outcome.benchmarks_warning} warn</span>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {fmt.dateTime(outcome.created_at)}
        </p>
      </div>
    </div>
  );
};

const LeakageItem: React.FC<{ summary: LeakageSummary }> = ({ summary }) => {
  const leakageColor = summary.leakage_rate > 0.2 ? 'red' : summary.leakage_rate > 0.1 ? 'yellow' : 'green';

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
      <div className="flex-1">
        <p className="font-medium text-gray-900 dark:text-gray-100">{summary.workflow_name}</p>
        {summary.workflow_type && (
          <p className="text-sm text-gray-600 dark:text-gray-400">{summary.workflow_type}</p>
        )}
        {summary.most_common_failure_step && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            Most failures at: {summary.most_common_failure_step}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className={`font-semibold text-${leakageColor}-600 dark:text-${leakageColor}-400`}>
          {(summary.leakage_rate * 100).toFixed(1)}% leakage
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {summary.completed_workflows}/{summary.total_workflows} completed
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Avg completion: {(summary.avg_completion_rate * 100).toFixed(1)}%
        </p>
      </div>
    </div>
  );
};

const WorkflowItem: React.FC<{ workflow: AIWorkflow }> = ({ workflow }) => {
  const statusColors: Record<string, string> = {
    active: 'blue',
    completed: 'green',
    failed: 'red',
    cancelled: 'gray'
  };
  const color = statusColors[workflow.status] || 'gray';

  const completionRate = workflow.total_steps > 0
    ? (workflow.completed_steps / workflow.total_steps) * 100
    : 0;

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`px-2 py-1 text-xs rounded-full bg-${color}-100 dark:bg-${color}-900/30 text-${color}-700 dark:text-${color}-300 font-medium`}>
            {workflow.status}
          </span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{workflow.workflow_name}</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {workflow.workflow_type || 'General'} • Triggered: {workflow.trigger_type}
        </p>
      </div>
      <div className="text-right">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {workflow.completed_steps}/{workflow.total_steps} steps
          </span>
          {workflow.failed_steps > 0 && (
            <span className="text-xs text-red-600 dark:text-red-400">
              {workflow.failed_steps} failed
            </span>
          )}
        </div>
        <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
          <div
            className={`h-full rounded-full bg-${color}-600`}
            style={{ width: `${completionRate}%` }}
          />
        </div>
        {workflow.total_duration_ms && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {(workflow.total_duration_ms / 1000).toFixed(1)}s
          </p>
        )}
      </div>
    </div>
  );
};

const EmployerWorkflowItem: React.FC<{ workflow: EmployerAgenticWorkflow }> = ({ workflow }) => {
  const { fmt } = useFormattedDate();
  const statusColors: Record<string, string> = {
    queued: 'gray',
    in_progress: 'blue',
    waiting_response: 'yellow',
    completed: 'green',
    failed: 'red',
    cancelled: 'gray'
  };
  const color = statusColors[workflow.status] || 'gray';

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-600">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className={`px-2 py-1 text-xs rounded-full bg-${color}-100 dark:bg-${color}-900/30 text-${color}-700 dark:text-${color}-300 font-medium`}>
            {workflow.status.replace('_', ' ')}
          </span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{workflow.employee_name}</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {workflow.template_name} • {workflow.employer_name || 'Employer'}
        </p>
        {workflow.ai_models_used && workflow.ai_models_used.length > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <Brain className="h-3 w-3 text-blue-500" />
            <span className="text-xs text-blue-600 dark:text-blue-400">
              {workflow.ai_models_used.join(', ')}
            </span>
          </div>
        )}
      </div>
      <div className="text-right">
        <div className="flex items-center gap-3 mb-1">
          {workflow.total_ai_tokens_used > 0 && (
            <span className="text-xs text-gray-600 dark:text-gray-400">
              {workflow.total_ai_tokens_used.toLocaleString()} tokens
            </span>
          )}
          {workflow.ai_decisions_count > 0 && (
            <span className="text-xs text-blue-600 dark:text-blue-400">
              {workflow.ai_decisions_count} decisions
            </span>
          )}
        </div>
        {workflow.outcome && (
          <span className={`text-xs ${workflow.outcome === 'success' ? 'text-green-600' : workflow.outcome === 'declined' ? 'text-red-600' : 'text-gray-600'}`}>
            {workflow.outcome}
          </span>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {workflow.initiated_at ? fmt.dateTime(workflow.initiated_at) : ''}
        </p>
        {workflow.duration_seconds !== undefined && workflow.duration_seconds > 0 && (
          <p className="text-xs text-gray-400">
            {workflow.duration_seconds >= 3600
              ? `${Math.floor(workflow.duration_seconds / 3600)}h ${Math.floor((workflow.duration_seconds % 3600) / 60)}m`
              : workflow.duration_seconds >= 60
                ? `${Math.floor(workflow.duration_seconds / 60)}m ${Math.floor(workflow.duration_seconds % 60)}s`
                : `${workflow.duration_seconds}s`
            }
          </p>
        )}
      </div>
    </div>
  );
};
